import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, RefreshCw, Building2, CreditCard, Calendar, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TenantsAdmin = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    slug: '',
    db_name: '',
    cnpj: '',
    email_financeiro: '',
    telefone: '',
    subscription_status: 'trialing',
    subscription_plan: '',
  });

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/admin/tenants`);
      const data = await response.json();
      setTenants(data);
    } catch (error) {
      toast.error('Erro ao carregar tenants');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingTenant) {
        // Atualizar
        const response = await fetch(`${API_URL}/api/admin/tenants/${editingTenant.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        
        if (response.ok) {
          toast.success('Tenant atualizado com sucesso');
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Erro ao atualizar');
        }
      } else {
        // Criar novo
        const response = await fetch(`${API_URL}/api/admin/tenants`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        
        if (response.ok) {
          toast.success('Tenant criado com sucesso');
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Erro ao criar');
        }
      }
      
      setShowDialog(false);
      resetForm();
      loadTenants();
    } catch (error) {
      toast.error('Erro ao salvar tenant');
      console.error(error);
    }
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    setFormData({
      nome: tenant.nome || '',
      slug: tenant.slug || '',
      db_name: tenant.db_name || '',
      cnpj: tenant.cnpj || '',
      email_financeiro: tenant.email_financeiro || '',
      telefone: tenant.telefone || '',
      subscription_status: tenant.subscription_status || 'trialing',
      subscription_plan: tenant.subscription_plan || '',
    });
    setShowDialog(true);
  };

  const handleDelete = async (tenant) => {
    if (!window.confirm(`Deseja desativar o tenant "${tenant.nome}"?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/tenants/${tenant.id}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        toast.success('Tenant desativado');
        loadTenants();
      } else {
        toast.error('Erro ao desativar tenant');
      }
    } catch (error) {
      toast.error('Erro ao desativar tenant');
      console.error(error);
    }
  };

  const resetForm = () => {
    setEditingTenant(null);
    setFormData({
      nome: '',
      slug: '',
      db_name: '',
      cnpj: '',
      email_financeiro: '',
      telefone: '',
      subscription_status: 'trialing',
      subscription_plan: '',
    });
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Ativo', variant: 'default', className: 'bg-emerald-500' },
      trialing: { label: 'Trial', variant: 'secondary', className: 'bg-blue-500 text-white' },
      past_due: { label: 'Inadimplente', variant: 'destructive', className: '' },
      canceled: { label: 'Cancelado', variant: 'outline', className: 'text-gray-500' },
    };
    
    const config = statusConfig[status] || { label: status, variant: 'outline', className: '' };
    
    return (
      <Badge variant={config.variant} className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Administração de Tenants</h1>
          <p className="text-muted-foreground">Gerencie os clientes e suas assinaturas</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadTenants} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button onClick={openNewDialog}>
            <Plus className="w-4 h-4 mr-2" />
            Novo Tenant
          </Button>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{tenants.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <CreditCard className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Ativos</p>
                <p className="text-2xl font-bold">{tenants.filter(t => t.subscription_status === 'active').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Em Trial</p>
                <p className="text-2xl font-bold">{tenants.filter(t => t.subscription_status === 'trialing').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Inadimplentes</p>
                <p className="text-2xl font-bold">{tenants.filter(t => t.subscription_status === 'past_due').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabela de Tenants */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Tenants</CardTitle>
          <CardDescription>Todos os clientes cadastrados no sistema</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Slug / URL</TableHead>
                <TableHead>Banco</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Plano</TableHead>
                <TableHead>Expira em</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenants.map((tenant) => (
                <TableRow key={tenant.id} className={!tenant.is_active ? 'opacity-50' : ''}>
                  <TableCell className="font-medium">{tenant.nome}</TableCell>
                  <TableCell>
                    <code className="text-xs bg-muted px-2 py-1 rounded">
                      {tenant.slug}.agrolinkbr.com.br
                    </code>
                  </TableCell>
                  <TableCell>
                    <code className="text-xs bg-muted px-2 py-1 rounded">
                      {tenant.db_name}
                    </code>
                  </TableCell>
                  <TableCell>{getStatusBadge(tenant.subscription_status)}</TableCell>
                  <TableCell>
                    {tenant.subscription_plan === 'monthly' && 'Mensal'}
                    {tenant.subscription_plan === 'yearly' && 'Anual'}
                    {!tenant.subscription_plan && '-'}
                  </TableCell>
                  <TableCell>
                    {tenant.subscription_status === 'trialing' 
                      ? formatDate(tenant.trial_ends_at)
                      : formatDate(tenant.current_period_end)
                    }
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(tenant)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {tenant.is_active && (
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(tenant)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {tenants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    Nenhum tenant cadastrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog de Criar/Editar */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTenant ? 'Editar Tenant' : 'Novo Tenant'}</DialogTitle>
            <DialogDescription>
              {editingTenant 
                ? 'Atualize as informações do tenant'
                : 'Preencha os dados para criar um novo cliente'
              }
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome da Empresa *</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Fazenda XYZ"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cnpj">CNPJ</Label>
                <Input
                  id="cnpj"
                  value={formData.cnpj}
                  onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                  placeholder="00.000.000/0001-00"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="slug">Slug (subdomínio) *</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') })}
                  placeholder="xyz"
                  required
                  disabled={!!editingTenant}
                />
                <p className="text-xs text-muted-foreground">
                  URL: {formData.slug || 'xxx'}.agrolinkbr.com.br
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="db_name">Nome do Banco *</Label>
                <Input
                  id="db_name"
                  value={formData.db_name}
                  onChange={(e) => setFormData({ ...formData, db_name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                  placeholder="xyz"
                  required
                  disabled={!!editingTenant}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email_financeiro">Email Financeiro</Label>
                <Input
                  id="email_financeiro"
                  type="email"
                  value={formData.email_financeiro}
                  onChange={(e) => setFormData({ ...formData, email_financeiro: e.target.value })}
                  placeholder="financeiro@empresa.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefone">Telefone</Label>
                <Input
                  id="telefone"
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  placeholder="(67) 99999-9999"
                />
              </div>
            </div>

            {editingTenant && (
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label htmlFor="subscription_status">Status da Assinatura</Label>
                  <Select
                    value={formData.subscription_status}
                    onValueChange={(v) => setFormData({ ...formData, subscription_status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trialing">Trial</SelectItem>
                      <SelectItem value="active">Ativo</SelectItem>
                      <SelectItem value="past_due">Inadimplente</SelectItem>
                      <SelectItem value="canceled">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subscription_plan">Plano</Label>
                  <Select
                    value={formData.subscription_plan || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, subscription_plan: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      <SelectItem value="monthly">Mensal (R$ 369,90)</SelectItem>
                      <SelectItem value="yearly">Anual (R$ 3.500,00)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {editingTenant ? 'Salvar' : 'Criar Tenant'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantsAdmin;
